/**
 * Ponte entre o splash estático de `index.html` (removido por main.ts assim
 * que a 1ª rota "termina de montar", via `router.ts`'s `onFirstRender`) e
 * páginas React que têm carregamento assíncrono interno (ex.: dashboard do
 * cotista, que busca saudação/empreendimentos/estatísticas/comunicados em
 * paralelo via `useEffect`).
 *
 * Sem isso, o router considera a rota "pronta" no instante em que
 * `ReactDOM.render()` monta a árvore — antes de qualquer `useEffect` de fetch
 * resolver — e o usuário vê o splash sumir só pra cair numa tela com vários
 * spinners individuais (um por seção) aparecendo em sequência.
 *
 * Uma página que precisa disso chama `holdInitialSplash()` de forma síncrona
 * (no corpo da própria função `renderXPage`, antes de montar) e, mais tarde,
 * chama `releaseInitialSplash()` quando seus dados essenciais da 1ª pintura
 * terminaram de carregar — sucesso ou erro, o que importa é não estar mais
 * "carregando". Páginas que nunca chamam `holdInitialSplash()` não são
 * afetadas: o gate nasce resolvido.
 *
 * `timeoutMs` é uma rede de segurança — se a página nunca chamar
 * `releaseInitialSplash()` (bug, endpoint que nunca resolve nem rejeita), o
 * splash não pode ficar preso pra sempre.
 */
import { useEffect, useRef } from "react";

let gate: Promise<void> = Promise.resolve();
let release: (() => void) | null = null;

export function holdInitialSplash(timeoutMs = 10000): void {
  release?.(); // um hold anterior nunca liberado (ex.: troca de rota rápida) não pode vazar pro próximo
  gate = new Promise<void>((resolve) => {
    release = resolve;
    setTimeout(resolve, timeoutMs);
  });
}

export function releaseInitialSplash(): void {
  release?.();
  release = null;
}

export function waitForInitialSplash(): Promise<void> {
  return gate;
}

/**
 * Versão em hook de `releaseInitialSplash()` para páginas cujo "pronto" já é
 * expresso por um único booleano derivado do próprio estado de carregamento
 * (ex.: `status !== "loading"`, `lista !== null`) — dispensa reimplementar o
 * `useRef` de "só uma vez" feito à mão em `dashboard.tsx`.
 *
 * Chamar incondicionalmente no topo do componente (regra de hooks), com
 * `ready` recalculado a cada render. Sucesso ou erro tanto faz — o que
 * importa pro splash é a página ter saído do estado "carregando".
 */
export function useSplashReady(ready: boolean): void {
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current || !ready) return;
    fired.current = true;
    releaseInitialSplash();
  }, [ready]);
}
